import { Injectable, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { debounce, sanitizeHtml } from 'src/app/core/common';
import { ReadingService } from '../text/reading.service';
import { ReadingActions } from 'src/app/core/enums';
import { environment } from 'src/environments/environment';
import { SettingsService } from '../settings/settings.service';
import { ISettings } from 'src/app/core/interfaces';

@Injectable({
  providedIn: 'root'
})
export class SpellCheckDialogService implements OnDestroy {
  subscriptions: Subscription[] = [];
  dialogActive = false;
  currentDialog: HTMLElement | null;
  suggestionsWrapper: HTMLElement;
  submenusWrapper: HTMLElement;
  suggestions: string[] = [];

  speakSuggestions: boolean = false;

  private defaultDialogClassName = 'asg-spellchecker-dialog';
  private defaultDialogContentWrapperClassName = 'asg-spellchecker-dialog-content';
  private defaultDialogSubmenuClassName = 'asg-spellchecker-dialog-submenu';
  private defaultArrowClassName = 'asg-spellchecker-dialog-arrow';
  private linksClassName = 'asg-spellcheker-link';
  private submenus = [
    'SPELLCHECKER.IGNORE',
    'SPELLCHECKER.IGNORE_ALL',
    'SPELLCHECKER.ADD_TO_WORDLIST',
  ];
  private translatedSubmenus: string[]= [];
  private showMoreLinkText = 'COMMON.SHOW_MORE';
  private hideMoreLinkText = 'COMMON.HIDE_MORE';
  private showMoreTransaltedLinkText = 'Show more';
  private hideMoreTransaltedLinkText = 'Hide';
  private showMoreActive = false;

  @Output() ignoreAllEv: EventEmitter<boolean> = new EventEmitter();
  @Output() ignoreWordEv: EventEmitter<boolean> = new EventEmitter();
  @Output() addWordEv: EventEmitter<boolean> = new EventEmitter();
  @Output() replaceWordEv: EventEmitter<string> = new EventEmitter();

  constructor(
    private readonly translate: TranslateService,
    private readonly readingService: ReadingService,
    private readonly settingsService: SettingsService,
  ) {
    this.initSubscirptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(x => x.unsubscribe());
  }

  showMore = () => {
    this.showMoreActive = !this.showMoreActive;
    if (this.showMoreActive) {
      this.drawContent(true);
    } else {
      this.drawContent();
    }
  }

  replaceWordToSuggested = (word: string) => {
    return () => {
      this.replaceWordEv.emit(word);
    };
  }

  ignoreWord = () => {
      this.ignoreWordEv.emit(true);
  }

  ignoreAll = () => {
    this.ignoreAllEv.emit(true);
  }

  rememberWord = () => {
    this.addWordEv.emit(true);
  }

  drawTheDialog(pos: {x: number, y: number}, suggestions: string[] | [] = []) {
    if (pos === null) {
      return;
    }

    if (this.dialogActive) {
      this.hideDialog();
    }

    const arrowOffest = 45;
    const calcLeft = pos.x - arrowOffest;
    const top = pos.y;
    const left = calcLeft <= 0 ? 0 : calcLeft;
    this.suggestions = suggestions;

    // draw container
    this.currentDialog = document.createElement('div');
    this.currentDialog.addEventListener('click', this.dialogClickhandler);
    this.drawCloseArrow();
    document.addEventListener('click', this.clickHandler);
    const style = `top:${top}px;left:${left}px;`;
    this.currentDialog.style.cssText = style;
    this.currentDialog.className = this.defaultDialogClassName;

    // draw arrow
    const arrow = document.createElement('div');
    arrow.className = this.defaultArrowClassName;
    this.currentDialog.appendChild(arrow);

    // draw content of suggestions
    this.drawContent();

    // draw submenus
    this.drawSubmenus();

    document.body.appendChild(this.currentDialog);

    this.dialogActive = true;
  }

  private clickHandler = (e: MouseEvent) => {
    if ((e.target as HTMLElement).contains(this.currentDialog)) {
      return;
    } else {
      this.hideDialog();
    }
  }

  private dialogClickhandler = (e: MouseEvent) => {
    e.stopPropagation();
    return false;
  }

  private drawCloseArrow() {
    const topBar = document.createElement('div');
    const closeIcon = document.createElement('i');
    closeIcon.addEventListener('click', this.hideDialog);
    closeIcon.className = 'fas fa-times';
    topBar.appendChild(closeIcon);
    if(this.currentDialog){
      this.currentDialog.appendChild(topBar);
    }
  }

  private drawContent(redraw: boolean = false) {
    if (this.suggestionsWrapper) {
      this.suggestionsWrapper.remove();
    }

    const hoverHandler = this.speakSuggestions ? 
      debounce(this, environment.speech.hoverSpeakDelay, this.onWordHovered, true) :
      () => {};

    this.suggestionsWrapper = document.createElement('div');
    this.suggestionsWrapper.className = this.defaultDialogContentWrapperClassName;

    const last = this.suggestions.length - 1;
    this.suggestions.forEach((word, idx) => {
      // sanitize the word
      word = sanitizeHtml(word);

      const a = document.createElement('a');
      a.className = this.linksClassName;
      a.addEventListener('mousemove', hoverHandler);
      if (!redraw) {
        if (idx < 5) {
          a.innerHTML = word;
          a.addEventListener('click', this.replaceWordToSuggested(word) as EventListener);
          this.suggestionsWrapper.appendChild(a);
        } else if (idx === 5) {
          const safeTranslatedText = sanitizeHtml(this.showMoreTransaltedLinkText);
          a.innerHTML = '<i class="fas fa-ellipsis-h"></i> ' + safeTranslatedText;
          a.addEventListener('click', this.showMore);
          this.suggestionsWrapper.appendChild(a);
        }
      } else {
        a.innerHTML = word;
        a.addEventListener('click', this.replaceWordToSuggested(word) as EventListener);
        this.suggestionsWrapper.appendChild(a);
      }
      if (idx === last && redraw) {
        const safeTranslatedText = sanitizeHtml(this.hideMoreTransaltedLinkText);
        const hide = document.createElement('a');
        hide.className = this.linksClassName;
        hide.innerHTML = '<i class="fas fa-ellipsis-h"></i> ' + safeTranslatedText;
        hide.addEventListener('click', this.showMore);
        this.suggestionsWrapper.appendChild(hide);
      }
    });

    if(this.currentDialog){
      if (this.submenusWrapper) {
        this.currentDialog.insertBefore(this.suggestionsWrapper, this.currentDialog.childNodes[1]);
      } else {
        this.currentDialog.appendChild(this.suggestionsWrapper);
      }
    }
  }

  private drawSubmenus() {
    this.submenusWrapper = document.createElement('div');
    this.submenusWrapper.className = this.defaultDialogSubmenuClassName;
    this.translatedSubmenus.forEach((word, idx) => {
      // menu items defined up top, shouldn't be sanitized
      // but just to be safe
      word = sanitizeHtml(word);
      const a = document.createElement('a');
      a.className = this.linksClassName;

      if (idx === 0) {
        a.addEventListener('click', this.ignoreWord);
        a.innerHTML = `<i class="fas fa-minus-circle"></i> ${word}`;
      } else if (idx === 1) {
        a.addEventListener('click', this.ignoreAll);
        a.innerHTML = `<i class="fas fa-minus-circle"></i> ${word}`;
      } else if (idx === 2) {
        a.addEventListener('click', this.rememberWord);
        a.innerHTML = `<i class="fas fa-plus-circle"></i> ${word}`;
      }
      this.submenusWrapper.appendChild(a);
    });
    if(this.currentDialog){
      this.currentDialog.appendChild(this.submenusWrapper);
    }
  }

  hideDialog = () => {
    if (this.currentDialog) {
      document.removeEventListener('click', this.clickHandler);
      this.currentDialog.removeEventListener('click', this.dialogClickhandler);
      this.currentDialog.remove();
      this.currentDialog = null;
      this.dialogActive = false;
    }
  }

  private initSubscirptions() {
    this.getTranslations();
    this.translate.onLangChange.subscribe(() => {
      this.getTranslations();
    });

    this.subscriptions.push(this.settingsService.getSettingsObservable()
      .subscribe((s: ISettings) => this.speakSuggestions = s.readSpellChecks));
  }

  private getTranslations() {
    this.translatedSubmenus = [];
    this.submenus.forEach(i => {
      this.subscriptions.push(this.translate.get(i)
        .subscribe((r:string) => {
          this.translatedSubmenus.push(r);
        }));
    });
    this.subscriptions.push(this.translate.get(this.showMoreLinkText)
      .subscribe(r => {
        this.showMoreTransaltedLinkText = r;
      }));
    this.subscriptions.push(this.translate.get(this.hideMoreLinkText)
      .subscribe(r => {
        this.hideMoreTransaltedLinkText = r;
      }));
  }

  private onWordHovered(e: MouseEvent) {
    if (e.target instanceof HTMLElement) {
      const element = e.target as HTMLElement;

      if (element.isSameNode(document.querySelector('a:hover'))) {
        this.readingService.runAction(ReadingActions.Stop);
        if(element && element.textContent){
          this.readingService.readSimpleText(element.textContent);
        }
      }
    }
  }
}
